import { useState } from '#imports'
import { computed } from 'vue'

import type { ProductCardProduct } from '../components/ProductCard/ProductCard.types'

import { useAlgoliaRecommendations } from './useAlgoliaRecommendations/useAlgoliaRecommendations'

interface AddToCartPopoverState {
  isVisible: boolean
  product: ProductCardProduct | null
  hasError: boolean
  errorBlocking: boolean
  productAlgoliaObjectId: string
  isMinimumDelayElapsed: boolean
}

const MINIMUM_DISPLAY_DURATION_MS = 500

export function useAddToCartPopover() {
  const state = useState<AddToCartPopoverState>('add-to-cart-popover', () => ({
    isVisible: false,
    product: null,
    hasError: false,
    errorBlocking: true,
    productAlgoliaObjectId: '',
    isMinimumDelayElapsed: true,
  }))

  const productAlgoliaObjectIds = computed(() =>
    state.value.productAlgoliaObjectId
      ? [state.value.productAlgoliaObjectId]
      : [],
  )

  const { data: recommendationsRaw, refresh: refreshRecommendations } =
    useAlgoliaRecommendations(productAlgoliaObjectIds, 'bought-together', {
      maxRecommendations: 2,
      queryParameters: {
        ruleContexts: ['only_accessory', 'only_compatible'],
      },
    })

  const recommendations = computed(() => {
    if (!state.value.isMinimumDelayElapsed) return []

    return (
      recommendationsRaw.value?.map(
        (product): ProductCardProduct => ({
          description: product.description,
          discountedPrice: product.discountedPrice,
          imageSrc: product.image,
          listingId: product.listingId,
          price: product.price,
          priceNew: product.priceNew,
          reviewRating: product.reviewRating,
          title: product.title,
          url: product.productPageLink,
          grade: product.grade,
          warranty: product.warrantyDuration,
          deal: product.deal,
          rawProduct: product,
          granularity: product.granularity,
          specialOfferType: product.specialOfferType,
          marketingTags: product.marketingTags,
        }),
      ) ?? []
    )
  })

  function getProductAlgoliaObjectId(product: ProductCardProduct) {
    if (!product.grade?.value) return product.rawProduct.id

    return `${product.rawProduct.id}_${product.grade.value}_0`
  }

  async function show(product: ProductCardProduct) {
    state.value.product = product
    state.value.isVisible = true
    state.value.hasError = false
    state.value.isMinimumDelayElapsed = false

    state.value.productAlgoliaObjectId = getProductAlgoliaObjectId(product)

    const delay = new Promise((resolve) => {
      setTimeout(() => {
        state.value.isMinimumDelayElapsed = true
        resolve(undefined)
      }, MINIMUM_DISPLAY_DURATION_MS)
    })

    await Promise.all([refreshRecommendations(), delay])
  }

  function showError(product?: ProductCardProduct, blocking = true) {
    state.value.errorBlocking = blocking
    state.value.product = blocking ? null : (product ?? state.value.product)
    state.value.isVisible = true
    state.value.hasError = true
  }

  function hide() {
    state.value.isVisible = false
    state.value.hasError = false
  }

  return {
    isVisible: computed(() => state.value.isVisible),
    product: computed(() => state.value.product),
    hasError: computed(() => state.value.hasError),
    errorBlocking: computed(() => state.value.errorBlocking),
    recommendations,
    show,
    showError,
    hide,
  }
}
